export const LOG_ROLES_BO = ["SUPERADMIN", "ADMIN", "MEMBER", "ACCOUNTANT", "DRIVER"] as const;
export const LOG_ROLES_CLIENT = ["SUPERADMIN", "ADMIN", "MEMBER", "ACCOUNTANT"] as const;
export type LogRole = (typeof LOG_ROLES_BO)[number];

export type NotificationVisibility = {
  inExpeditionNotes?: boolean;
  inExpeditionNotifications?: boolean;
  inGlobalNotifications?: boolean;
  isUrgent?: boolean;
};

export type SlackTemplate = {
  // If defined, the Slack message is posted to this channel.
  // If absent, the Slack message is DM'd to the notification recipients.
  channel?: string;
};

export type NotificationTemplate = {
  visibility: NotificationVisibility;
  // When true, the call-site MUST provide `userIds` and one notification per user is created.
  // When false/absent, a single global notification (recipient = null) is created.
  expectsRecipients?: boolean;
  slack?: SlackTemplate;
};

export interface LogTemplate {
  entityType: string;
  notification?: NotificationTemplate;
}

const LOG_TEMPLATES_RAW = {
  // ─── BILLING_ENTITY ───────────────────────────────────────────────────────
  BILLING_ENTITY_CREATE: {
    entityType: "BILLING_ENTITY",
  },
  BILLING_ENTITY_UPDATE: {
    entityType: "BILLING_ENTITY",
  },
  BILLING_ENTITY_DELETE: {
    entityType: "BILLING_ENTITY",
  },
  BILLING_ENTITY_CREATE_MANDATE: {
    entityType: "BILLING_ENTITY",
  },
  BILLING_ENTITY_CANCEL_MANDATE: {
    entityType: "BILLING_ENTITY",
  },

  // ─── COMPANY ──────────────────────────────────────────────────────────────
  COMPANY_UPDATE_BO: {
    entityType: "COMPANY",
  },
  COMPANY_UPDATE: {
    entityType: "COMPANY",
  },
  COMPANY_BLOCK: {
    entityType: "COMPANY",
  },
  COMPANY_UNBLOCK: {
    entityType: "COMPANY",
  },
  COMPANY_UNBLOCK_EMAIL: {
    entityType: "COMPANY",
  },

  // ─── CUSTOM ───────────────────────────────────────────────────────────────
  CUSTOM_CREATE: {
    entityType: "CUSTOM",
  },
  CUSTOM_UPDATE: {
    entityType: "CUSTOM",
  },
  CUSTOM_DELETE: {
    entityType: "CUSTOM",
  },

  // ─── CUSTOM_POLICY ────────────────────────────────────────────────────────
  CUSTOM_POLICY_UPDATE: {
    entityType: "CUSTOM_POLICY",
  },

  // ─── DC_INVOICE ───────────────────────────────────────────────────────────
  DC_INVOICE_NOT_READY: {
    entityType: "DC_INVOICE",
  },
  DC_INVOICE_UNMATCHED: {
    entityType: "DC_INVOICE",
  },
  DC_INVOICE_ALREADY_IMPORTED: {
    entityType: "DC_INVOICE",
  },

  // ─── EXPEDITION ───────────────────────────────────────────────────────────
  EXPEDITION_DEBUG: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_CLIENTBO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_CLIENT: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_DC_QUOTATION: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPDATE_CLIENT: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inExpeditionNotifications: true },
    },
  },
  EXPEDITION_DELETE_CLIENT: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_VALIDATE_CLIENT: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CANCEL: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_PRE_FINALIZE: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPDATE_PRE_FINALIZE: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPDATE_FINALIZE_QUOTATION: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_FINALIZE: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_FINALIZE_FAILED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_FINALIZE_TIMEOUT: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_FINALIZE_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPDATE_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_NOTE_BO: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inGlobalNotifications: true },
      expectsRecipients: true,
      slack: {},
    },
  },
  EXPEDITION_NOTE_DELETE_BO: {
    entityType: "EXPEDITION",
  },
  // Is called Urgent but is not anymore, behaviour was asked to change
  // No notification if expedition in "suivi" but notification otherwise
  // Can didn't change log's name to avoid problems with pre-existing logs.
  EXPEDITION_URGENT_NOTE_BO: {
    entityType: "EXPEDITION",
    notification: {
      visibility: {},
      expectsRecipients: true,
      slack: {},
    },
  },
  EXPEDITION_LATE_SHIPPING_QUALIFICATION_BO: {
    entityType: "EXPEDITION",
    notification: {
      visibility: {},
      expectsRecipients: true,
      slack: {},
    },
  },
  EXPEDITION_SWITCH_EXPRESS: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inExpeditionNotifications: true, inGlobalNotifications: true },
      expectsRecipients: true,
      slack: {},
    },
  },
  EXPEDITION_UPDATE_RETURN: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPDATE_REQUEST: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inExpeditionNotifications: true, inGlobalNotifications: true, isUrgent: true },
    },
  },
  EXPEDITION_DELETE_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_PERMANENT_DELETE_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_RECOVER_BO: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CLEANUP_STRATUS: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CLEANUP_FEDEX: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_ASSIGN: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_PRINT_SHIPPING_LABEL: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_URGENT_ASSIGN: {
    entityType: "EXPEDITION",
    notification: {
      visibility: {
        inGlobalNotifications: true,
        isUrgent: true,
      },
      expectsRecipients: true,
      slack: {},
    },
  },
  EXPEDITION_DELETE_REQUEST: {
    entityType: "EXPEDITION",
    notification: {
      visibility: {
        inExpeditionNotifications: true,
        inGlobalNotifications: true,
        isUrgent: true,
      },
    },
  },
  EXPEDITION_DELETE_REQUEST_CANCEL: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inExpeditionNotifications: true, inGlobalNotifications: true },
    },
  },
  EXPEDITION_DELETE_EXPIRED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_PN_STRATUS_FAILED: {
    entityType: "EXPEDITION",
    notification: {
      visibility: {
        inExpeditionNotifications: true,
        inGlobalNotifications: true,
        isUrgent: true,
      },
    },
  },
  EXPEDITION_BLOCK: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UNBLOCK: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_DELIVERED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_POSTPONE_PICKUP: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_EXPRESS_PICKUP_UPDATE: {
    entityType: "EXPEDITION",
    notification: {
      visibility: { inExpeditionNotifications: true, inGlobalNotifications: true },
    },
  },
  EXPEDITION_RESET_DRAFT_OWNER: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_MARK_DHL_TNT_SHIPPED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_ORDER_SUCCESS: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_ORDER_FAILED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_SHIPMENT_SUCCESS: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_CREATE_SHIPMENT_FAILED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_GENERATE_ORDERFORM: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_GENERATE_EXPEDITIONFORM: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_GENERATE_QUOTE: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPLOAD_DOCUMENTS_TO_FEDEX_SUCCESS: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_UPLOAD_DOCUMENTS_TO_FEDEX_FAILED: {
    entityType: "EXPEDITION",
  },
  EXPEDITION_FILE_UPDATE_ERROR: {
    entityType: "EXPEDITION",
  },

  // ─── FILE ─────────────────────────────────────────────────────────────────
  FILE_UPLOAD_TO_FEDEX_SUCCESS: {
    entityType: "FILE",
  },

  // ─── GLOBAL_PARAMETERS ────────────────────────────────────────────────────
  GLOBAL_PARAMETERS_UPDATE: {
    entityType: "GLOBAL_PARAMETERS",
  },

  // ─── IMPORTER ─────────────────────────────────────────────────────────────
  IMPORTER_CREATE: {
    entityType: "IMPORTER",
  },
  IMPORTER_UPDATE: {
    entityType: "IMPORTER",
  },
  IMPORTER_DELETE: {
    entityType: "IMPORTER",
  },

  // ─── INVOICE ──────────────────────────────────────────────────────────────
  INVOICE_ERROR: {
    entityType: "INVOICE",
  },
  INVOICE_CREATE: {
    entityType: "INVOICE",
  },
  INVOICE_UPDATE: {
    entityType: "INVOICE",
  },
  INVOICE_DELETE: {
    entityType: "INVOICE",
  },
  INVOICE_SEND_EMAIL: {
    entityType: "INVOICE",
  },
  INVOICE_ERROR_EMAIL: {
    entityType: "INVOICE",
  },
  INVOICE_VALIDATE: {
    entityType: "INVOICE",
  },
  INVOICE_BATCH_VALIDATE_ERROR: {
    entityType: "INVOICE",
  },
  INVOICE_PROCESS_PAYMENT_ERROR: {
    entityType: "INVOICE",
  },
  INVOICE_UPDATE_PAYMENT_STATUS: {
    entityType: "INVOICE",
  },
  INVOICE_BLOCK: {
    entityType: "INVOICE",
  },
  INVOICE_BLOCK_EMAIL: {
    entityType: "INVOICE",
  },
  INVOICE_UNBLOCK: {
    entityType: "INVOICE",
  },

  // ─── LITIGE ───────────────────────────────────────────────────────────────
  LITIGE_CREATE: {
    entityType: "LITIGE",
  },

  // ─── MAILER ───────────────────────────────────────────────────────────────
  MAILER_SEND_MAIL_ERROR: {
    entityType: "MAILER",
  },

  // ─── PACKAGE_PRESET ───────────────────────────────────────────────────────
  PACKAGE_PRESET_CREATE: {
    entityType: "PACKAGE_PRESET",
  },
  PACKAGE_PRESET_UPDATE: {
    entityType: "PACKAGE_PRESET",
  },
  PACKAGE_PRESET_DELETE: {
    entityType: "PACKAGE_PRESET",
  },

  // ─── PAYMENT ──────────────────────────────────────────────────────────────
  PAYMENT_CREATE: {
    entityType: "PAYMENT",
  },
  PAYMENT_ERROR_CREATE: {
    entityType: "PAYMENT",
  },

  // ─── PICKUP_LOCATION ──────────────────────────────────────────────────────
  PICKUP_LOCATION_CREATE: {
    entityType: "PICKUP_LOCATION",
  },
  PICKUP_LOCATION_UPDATE: {
    entityType: "PICKUP_LOCATION",
  },
  PICKUP_LOCATION_DELETE: {
    entityType: "PICKUP_LOCATION",
  },

  // ─── PRODUCT ──────────────────────────────────────────────────────────────
  PRODUCT_CREATE: {
    entityType: "PRODUCT",
  },
  PRODUCT_UPDATE: {
    entityType: "PRODUCT",
  },
  PRODUCT_DELETE: {
    entityType: "PRODUCT",
  },

  // ─── PRODUCT_USA ──────────────────────────────────────────────────────────
  PRODUCT_USA_CREATE: {
    entityType: "PRODUCT_USA",
  },
  PRODUCT_USA_UPDATE: {
    entityType: "PRODUCT_USA",
  },
  PRODUCT_USA_DELETE: {
    entityType: "PRODUCT_USA",
  },

  // ─── RIB ──────────────────────────────────────────────────────────────────
  RIB_CREATE: {
    entityType: "RIB",
  },
  RIB_UPDATE: {
    entityType: "RIB",
  },
  RIB_DELETE: {
    entityType: "RIB",
  },

  // ─── TARIF ────────────────────────────────────────────────────────────────
  TARIF_CREATE: {
    entityType: "TARIF",
  },
  TARIF_UPDATE: {
    entityType: "TARIF",
  },
  TARIF_DELETE: {
    entityType: "TARIF",
  },

  // ─── USER ─────────────────────────────────────────────────────────────────
  USER_CREATE: {
    entityType: "USER",
  },
  USER_UPDATE: {
    entityType: "USER",
  },
  USER_DELETE: {
    entityType: "USER",
  },

  // ─── GOCARDLESS ───────────────────────────────────────────────────────────
  UNHANDLED_EVENT_GOCARDLESS: {
    entityType: "BILLING_ENTITY",
  },
  UNKNOWN_EVENT_GOCARDLESS: {
    entityType: "BILLING_ENTITY",
  },
} satisfies Record<string, LogTemplate>;

export type LogKey = keyof typeof LOG_TEMPLATES_RAW;

// Conserve les types propres à chaque clé. Utilisé pour avoir forme exacte du champ `notification` attendu pour chaque log key.
export type LogTemplates = typeof LOG_TEMPLATES_RAW;

export const LOG_TEMPLATES: Record<LogKey, LogTemplate> = LOG_TEMPLATES_RAW;

export const LOG_TEMPLATES_BY_ENTITY = (
  Object.entries(LOG_TEMPLATES) as [LogKey, LogTemplate][]
).reduce(
  (acc, [key, t]) => {
    if (!acc[t.entityType]) acc[t.entityType] = [];
    acc[t.entityType].push({ key, ...t });
    return acc;
  },
  {} as Record<string, ({ key: LogKey } & LogTemplate)[]>,
);

const LOG_TEMPLATE_ENTRIES = Object.entries(LOG_TEMPLATES) as [LogKey, LogTemplate][];

export const IN_GLOBAL_NOTIFICATIONS_LOG_KEYS: LogKey[] = LOG_TEMPLATE_ENTRIES.filter(
  ([, t]) => t.notification?.visibility.inGlobalNotifications,
).map(([k]) => k);

export const IN_EXPEDITION_NOTIFICATIONS_LOG_KEYS: LogKey[] = LOG_TEMPLATE_ENTRIES.filter(
  ([, t]) => t.notification?.visibility.inExpeditionNotifications,
).map(([k]) => k);

export const URGENT_LOG_KEYS: LogKey[] = LOG_TEMPLATE_ENTRIES.filter(
  ([, t]) => t.notification?.visibility.isUrgent,
).map(([k]) => k);
